// ─────────────────────────────────────────────────────────────────────────────
// V2Portal — public customer portal (/portal/:token) in the v2 design.
// Read-only, no auth. Uses exactly v1's public endpoints:
//   GET /api/portal/:token
//   GET /api/portal/:token/invoice/:invoiceId
//   GET /api/portal/:token/invoice/:invoiceId/pdf
// Customers keep seeing v1 until the business switches the default; the owner
// previews this with ?ui=v2 (the "Preview" button on the Clients page does that).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Money, Segmented, Sheet, Skel, inr, useMedia } from "./primitives";
import { ensureFont } from "./appearance";
import "./v2.css";
import "./v2-pages.css";

const BASE = `${API_BASE}/api`;
const fmtDate = (d, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-IN", opts) : "—";
const money2 = (v) => inr(v, { decimals: 2 });

const dueDateOf = (inv) => {
  if (inv.dueDate) return new Date(inv.dueDate);
  if (!inv.invoiceDate) return null;
  const d = new Date(inv.invoiceDate);
  d.setDate(d.getDate() + (inv.paymentTimeline || 30));
  return d;
};
const statusOf = (inv) => {
  if (inv.paymentStatus === "PAID") return { tone: "good", label: "Paid", key: "paid" };
  const due = dueDateOf(inv);
  if (due && due < new Date()) return { tone: "bad", label: "Overdue", key: "unpaid" };
  if (inv.paymentStatus === "PARTIAL") return { tone: "warn", label: "Part paid", key: "unpaid" };
  return { tone: "warn", label: "Due", key: "unpaid" };
};

function Status({ tone, children }) {
  const g = tone === "good" ? "check" : tone === "warn" ? "clock" : "alert";
  return <span className={`v2-status v2-status--${tone}`}><Glyph name={g} size={13} stroke={2.2} /> {children}</span>;
}

async function downloadPdf(token, inv, setBusy) {
  setBusy((b) => ({ ...b, [inv._id]: true }));
  try {
    const res = await fetch(`${BASE}/portal/${token}/invoice/${inv._id}/pdf`);
    if (!res.ok) throw new Error("PDF failed");
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = `Invoice-${inv.invoiceNumber}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch { return false; }
  finally { setBusy((b) => ({ ...b, [inv._id]: false })); }
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Portal() {
  const { token } = useParams();
  const isPhone = useMedia("(max-width: 640px)");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("invoices");
  const [filter, setFilter] = useState("all");
  const [openInv, setOpenInv] = useState(null);     // { summary, detail? }
  const [pdfBusy, setPdfBusy] = useState({});
  const [toast, setToast] = useState("");
  const originRef = useRef(null);

  useEffect(() => { ensureFont("jakarta"); }, []);

  useEffect(() => {
    if (!token) { setError("This link is incomplete."); setLoading(false); return; }
    fetch(`${BASE}/portal/${token}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Portal not found or has been disabled.");
        setData(j);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const name = data?.company?.companyName;
    document.title = name ? `${name} · Account statement` : "Account statement";
  }, [data]);

  const say = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const openInvoice = async (inv, e) => {
    originRef.current = e?.currentTarget || null;
    setOpenInv({ summary: inv, detail: null });
    try {
      const r = await fetch(`${BASE}/portal/${token}/invoice/${inv._id}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error);
      setOpenInv((o) => (o && o.summary._id === inv._id ? { ...o, detail: j } : o));
    } catch {
      setOpenInv((o) => (o ? { ...o, error: "Couldn't load this invoice." } : o));
    }
  };

  const pdf = async (inv) => {
    const ok = await downloadPdf(token, inv, setPdfBusy);
    say(ok ? `Downloaded ${inv.invoiceNumber}` : "Couldn't generate the PDF — please try again.");
  };

  const invoices = useMemo(() => data?.invoices || [], [data]);
  const counts = useMemo(() => ({
    all: invoices.length,
    unpaid: invoices.filter((i) => statusOf(i).key === "unpaid").length,
    paid: invoices.filter((i) => statusOf(i).key === "paid").length,
  }), [invoices]);
  const shown = invoices.filter((i) => filter === "all" || statusOf(i).key === filter);

  // Statement grouped by month (newest first, as delivered)
  const ledgerGroups = useMemo(() => {
    const m = new Map();
    (data?.recentLedger || []).forEach((e) => {
      const k = fmtDate(e.date, { month: "long", year: "numeric" });
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return [...m.entries()];
  }, [data]);

  // ── States ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="v2 v2-pub">
        <div className="v2-backdrop" aria-hidden="true" />
        <div className="v2pub-wrap">
          <Skel h={52} w={260} r={16} style={{ marginBottom: 40 }} />
          <Skel h={44} w="55%" r={12} style={{ marginBottom: 12 }} />
          <Skel h={20} w="35%" r={8} style={{ marginBottom: 28 }} />
          <Skel h={190} r={32} style={{ marginBottom: 20 }} />
          <Skel h={300} r={28} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2 v2-pub">
        <div className="v2-backdrop" aria-hidden="true" />
        <div className="v2pub-gone">
          <Duo name="alert" size={56} />
          <h1>This link isn't active</h1>
          <p>The statement you're looking for has been turned off or the link is incomplete. Please ask the business that shared it for a fresh link.</p>
        </div>
      </div>
    );
  }

  const { client, company = {}, summary } = data;
  const bal = summary.accountBalance ?? summary.totalOutstanding ?? 0;
  const settled = bal <= 0.005;
  const coName = company.companyName || "Your supplier";
  const logo = company.logoUrl ? (/^https?:/.test(company.logoUrl) ? company.logoUrl : `${API_BASE}${company.logoUrl}`) : null;
  const website = company.website ? (/^https?:/.test(company.website) ? company.website : `https://${company.website}`) : null;
  const today = fmtDate(new Date(), { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="v2 v2-pub">
      <div className="v2-backdrop" aria-hidden="true" />

      {/* ── Brand bar ─────────────────────────────────────────────────── */}
      <header className="v2pub-bar">
        <div className="v2pub-brand">
          {logo
            ? <img src={logo} alt="" className="v2pub-logo" />
            : <span className="v2pub-mono" aria-hidden="true">{coName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</span>}
          <span className="v2pub-brand-text">
            <strong>{coName}</strong>
            <span>Account statement</span>
          </span>
        </div>
        <div className="v2pub-contact">
          {company.phone && <a className="v2-btn v2-btn--sm" href={`tel:${company.phone}`}><Glyph name="phone" size={15} />{!isPhone && " Call"}</a>}
          {website && <a className="v2-btn v2-btn--sm" href={website} target="_blank" rel="noreferrer"><Glyph name="arrowUpRight" size={15} />{!isPhone && " Website"}</a>}
        </div>
      </header>

      <main className="v2pub-wrap">
        {/* ── Greeting ───────────────────────────────────────────────── */}
        <section className="v2pub-hello">
          <span className="v2pub-eyebrow">Prepared for</span>
          <h1>{client.clientName}</h1>
          <p>{client.clientGSTIN ? <>GSTIN <span className="v2-mono">{client.clientGSTIN}</span> · </> : null}Updated {today}</p>
        </section>

        {/* ── Balance ────────────────────────────────────────────────── */}
        <section className={`v2pub-balance${settled ? " is-settled" : ""}`} aria-label="Account balance">
          <div className="v2pub-balance-main">
            <span className="v2pub-balance-label">
              {settled ? <><Glyph name="check" size={16} stroke={2.4} /> {bal < -0.005 ? "In credit" : "All settled"}</> : "Balance due"}
            </span>
            <div className="v2pub-balance-amt"><Money value={Math.abs(bal)} /></div>
            <span className="v2pub-balance-note">
              {settled
                ? `Thank you — nothing is outstanding with ${coName}.`
                : `${summary.unpaidCount} invoice${summary.unpaidCount === 1 ? "" : "s"} awaiting payment`}
            </span>
          </div>
          <dl className="v2pub-stats">
            <div><dt>Invoiced</dt><dd>{money2(summary.totalInvoiced)}</dd></div>
            <div><dt>Paid</dt><dd>{money2(summary.totalPaid)}</dd></div>
            {summary.creditNotesTotal > 0 && <div><dt>Credit notes</dt><dd>{money2(summary.creditNotesTotal)}</dd></div>}
            <div><dt>Invoices</dt><dd>{summary.invoiceCount}</dd></div>
          </dl>
        </section>

        {/* ── Tabs ───────────────────────────────────────────────────── */}
        <div className="v2pub-tabs">
          <Segmented ariaLabel="View" value={tab} onChange={setTab} options={[
            { value: "invoices", label: `Invoices · ${invoices.length}` },
            { value: "statement", label: "Statement" },
          ]} />
          {tab === "invoices" && invoices.length > 0 && (
            <Segmented size="sm" ariaLabel="Filter invoices" value={filter} onChange={setFilter} options={[
              { value: "all", label: "All" },
              { value: "unpaid", label: `Unpaid${counts.unpaid ? ` ${counts.unpaid}` : ""}` },
              { value: "paid", label: "Paid" },
            ]} />
          )}
        </div>

        <section className="v2-card v2pub-panel" key={tab + filter}>
          {tab === "invoices" ? (
            shown.length === 0 ? (
              <div className="v2c-empty"><Duo name="invoice" size={44} /><span>{invoices.length ? "No invoices in this view." : "No invoices yet."}</span></div>
            ) : (
              <ul className="v2pub-invs">
                {shown.map((inv) => {
                  const st = statusOf(inv);
                  const due = dueDateOf(inv);
                  const bal = (inv.grandTotal || 0) - (inv.amountPaid || 0);
                  return (
                    <li key={inv._id}>
                      <button type="button" className="v2pub-inv" onClick={(e) => openInvoice(inv, e)}>
                        <Duo name="invoice" size={34} />
                        <span className="v2pub-inv-main">
                          <span className="v2pub-inv-no">{inv.invoiceNumber}</span>
                          <span className="v2pub-inv-meta">
                            {fmtDate(inv.invoiceDate)}
                            {st.key !== "paid" && due && <> · due {fmtDate(due, { day: "numeric", month: "short" })}</>}
                          </span>
                        </span>
                        <span className="v2pub-inv-end">
                          <span className="v2pub-inv-amt">{money2(inv.grandTotal)}</span>
                          {st.key !== "paid" && bal > 0 && bal < inv.grandTotal
                            ? <span className="v2pub-inv-meta">{money2(bal)} left</span>
                            : <Status tone={st.tone}>{st.label}</Status>}
                        </span>
                        <Glyph name="chevronRight" size={16} className="v2pub-chev" />
                      </button>
                      <button type="button" className="v2-icon-btn v2pub-dl" aria-label={`Download ${inv.invoiceNumber} as PDF`}
                        disabled={!!pdfBusy[inv._id]} onClick={() => pdf(inv)}>
                        {pdfBusy[inv._id] ? <span className="v2-spin" /> : <Glyph name="download" size={18} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : ledgerGroups.length === 0 ? (
            <div className="v2c-empty"><Duo name="pulse" size={44} /><span>No transactions yet.</span></div>
          ) : (
            <div className="v2pub-ledger">
              {ledgerGroups.map(([month, entries]) => (
                <div key={month}>
                  <div className="v2c-letter v2pub-month">{month}</div>
                  {entries.map((e, i) => {
                    const credit = e.type === "credit";
                    return (
                      <div key={e._id || i} className="v2pub-entry">
                        <span className="v2pub-day">
                          <strong>{new Date(e.date).getDate()}</strong>
                          <span>{fmtDate(e.date, { month: "short" })}</span>
                        </span>
                        <span className="v2pub-inv-main">
                          <span className="v2pub-entry-desc">{e.description || (credit ? "Payment received" : "Invoice")}</span>
                          <span className="v2pub-inv-meta">Balance {money2(e.balance)}</span>
                        </span>
                        <span className={`v2pub-entry-amt${credit ? " is-credit" : ""}`}>
                          {credit ? "−" : "+"}{money2(e.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
              <p className="v2pub-ledger-note">Showing the latest {data.recentLedger.length} transactions. Payments reduce your balance (−); invoices add to it (+).</p>
            </div>
          )}
        </section>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <footer className="v2pub-foot">
          <div>
            <strong>{coName}</strong>
            {company.companyAddress && <span>{company.companyAddress}</span>}
            {company.companyGSTIN && <span>GSTIN <span className="v2-mono">{company.companyGSTIN}</span></span>}
          </div>
          <p><Glyph name="shield" size={14} /> Shared privately with you. View-only · Powered by EazyBilling</p>
        </footer>
      </main>

      <InvoiceSheet state={openInv} originRef={originRef} onClose={() => setOpenInv(null)}
        busy={openInv ? !!pdfBusy[openInv.summary._id] : false} onPdf={() => pdf(openInv.summary)} coName={coName} />

      <div className={`v2pub-toast${toast ? " is-on" : ""}`} role="status" aria-live="polite">{toast}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Invoice detail — reads like a receipt
// ══════════════════════════════════════════════════════════════════════════════
function InvoiceSheet({ state, originRef, onClose, busy, onPdf, coName }) {
  const keep = useRef(state);
  if (state) keep.current = state;
  const s = keep.current;
  if (!s) return null;
  const inv = s.detail || s.summary;
  const st = statusOf(inv);
  const due = dueDateOf(inv);
  const bal = (inv.grandTotal || 0) - (inv.amountPaid || 0);
  const docType = (inv.documentType || "TAX_INVOICE").replace(/_/g, " ").toLowerCase();

  return (
    <Sheet open={!!state} onClose={onClose} originRef={originRef} width={620}
      title={inv.invoiceNumber} subtitle={`${docType.replace(/^\w/, (c) => c.toUpperCase())} from ${coName}`}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Close</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy} onClick={onPdf}>
          {busy ? "Preparing PDF…" : "Download PDF"}
        </button>
      </>}>
      <div className="v2pub-receipt">
        <div className="v2pub-receipt-top">
          <div>
            <span className="v2-label">{st.key === "paid" ? "Paid in full" : "Amount due"}</span>
            <div className="v2pub-receipt-amt"><Money value={st.key === "paid" ? inv.grandTotal : bal} /></div>
          </div>
          <Status tone={st.tone}>{st.label}</Status>
        </div>

        <dl className="v2pub-meta">
          <div><dt>Issued</dt><dd>{fmtDate(inv.invoiceDate)}</dd></div>
          <div><dt>{inv.dueDate ? "Due" : "Due (est.)"}</dt><dd>{due ? fmtDate(due) : "—"}</dd></div>
          <div><dt>Total</dt><dd>{money2(inv.grandTotal)}</dd></div>
          <div><dt>Paid</dt><dd>{money2(inv.amountPaid)}</dd></div>
        </dl>

        {s.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {s.error}</div>}

        {!s.detail && !s.error ? (
          <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={48} r={14} />)}</div>
        ) : s.detail && (
          <>
            <div className="v2c-section-title">Items</div>
            <div className="v2-inset v2pub-items">
              {(inv.items || []).map((it, i) => {
                const rate = it.unitPrice || it.price || 0;
                return (
                  <div key={i} className="v2-inset-row v2-inset-row--static v2pub-item">
                    <span className="v2-row-main">
                      <span className="v2-row-title v2-wrap">{it.name}</span>
                      <span className="v2-row-sub">
                        {it.qty} {it.unit || ""} × {money2(rate)}{it.gstRate !== undefined ? ` · GST ${it.gstRate}%` : ""}
                        {(it.hsnCode || it.hsnSacCode) ? ` · HSN ${it.hsnCode || it.hsnSacCode}` : ""}
                      </span>
                      {it.description && <span className="v2c-note">{it.description}</span>}
                    </span>
                    <b>{money2(rate * (it.qty || 0))}</b>
                  </div>
                );
              })}
            </div>

            <div className="v2-inset v2pub-totals">
              <div className="v2-inset-row v2-inset-row--static"><span>Subtotal</span><b>{money2(inv.subtotal)}</b></div>
              {inv.discountAmount > 0 && <div className="v2-inset-row v2-inset-row--static"><span>Discount{inv.discountPercent ? ` (${inv.discountPercent}%)` : ""}</span><b>−{money2(inv.discountAmount)}</b></div>}
              {inv.totalIgst > 0 && <div className="v2-inset-row v2-inset-row--static"><span>IGST</span><b>{money2(inv.totalIgst)}</b></div>}
              {inv.totalCgst > 0 && <div className="v2-inset-row v2-inset-row--static"><span>CGST</span><b>{money2(inv.totalCgst)}</b></div>}
              {inv.totalSgst > 0 && <div className="v2-inset-row v2-inset-row--static"><span>SGST</span><b>{money2(inv.totalSgst)}</b></div>}
              <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Total</span><b>{money2(inv.grandTotal)}</b></div>
            </div>

            {inv.irn && (
              <div className="v2pub-irn">
                <span className="v2-label">e-Invoice · IRN</span>
                <span className="v2-mono">{inv.irn}</span>
                {(inv.ackNo || inv.ackDate) && <span className="v2-row-sub">Ack {inv.ackNo || ""}{inv.ackDate ? ` · ${fmtDate(inv.ackDate)}` : ""}</span>}
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
